import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import { selectedFiatBeneficiaryAtom } from '../Pay/atoms/atoms'
import BeneficiaryForm from './components/BeneficiaryForm'
import { Complete } from './components/Complete'
import { Confirmation } from './components/Confirmation'
import { PaymentDetails } from './components/PaymentDetails'
import { useFiatSendForm } from './hooks/useFiatSendForm'

const FiatSend = () => {
  const { t } = useTranslation()

  const [walletAction, setWalletAction] = useContext(WalletActionContext)

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)

  const [step, setStep] = useState<FiatSendStep>(FiatSendSteps.BENEFICIARY)

  useFiatSendForm()

  const sheetHeader = useMemo(() => {
    switch (step) {
      case FiatSendSteps.BENEFICIARY:
        return t('wallets.fiatSend.beneficiaryStepHeader')
      case FiatSendSteps.AMOUNT:
        return t('wallets.fiatSend.amountStepHeader')
      case FiatSendSteps.CONFIRMATION:
        return t('wallets.fiatSend.confirmationHeader')
      case FiatSendSteps.EOF:
        return null
      default:
        return ''
    }
  }, [step])

  const canGoBack = useMemo(() => {
    switch (step) {
      case FiatSendSteps.AMOUNT:
      case FiatSendSteps.CONFIRMATION:
        return true
      case FiatSendSteps.BENEFICIARY:
      case FiatSendSteps.EOF:
      default:
        return false
    }
  }, [step])

  const handleOnBack = () => {
    if (canGoBack) {
      switch (step) {
        case FiatSendSteps.AMOUNT:
          setStep(FiatSendSteps.BENEFICIARY)
          setBeneficiaryAtom(null)
          return
        case FiatSendSteps.CONFIRMATION:
          setStep(FiatSendSteps.AMOUNT)
          return
        case FiatSendSteps.BENEFICIARY:
        case FiatSendSteps.EOF:
        default:
      }
    }
  }

  useEffect(() => {
    setWalletAction({
      ...walletAction,
      header: sheetHeader,
      onBack: canGoBack ? handleOnBack : undefined,
    })
  }, [step, canGoBack])

  return (
    <Box width="full">
      {step === FiatSendSteps.BENEFICIARY && <BeneficiaryForm setStep={setStep} />}
      {step === FiatSendSteps.AMOUNT && <PaymentDetails setStep={setStep} />}
      {step === FiatSendSteps.CONFIRMATION && <Confirmation setStep={setStep} />}
      {step === FiatSendSteps.EOF && <Complete />}
    </Box>
  )
}

export default memo(FiatSend)
